/**
 * RW-BUILD-005 — Offline queue for voice notes (TEXT ONLY).
 *
 * Transcription happens on-device (Web Speech API) before saving, so each
 * queued note is just a small JSON payload — localStorage is plenty. Notes
 * recorded while offline are flushed to POST /api/dashboard/voice-notes when
 * the connection returns. A stable `clientOfflineId` lets the backend de-dupe.
 *
 * No audio is uploaded and no Emergent LLM key is used anywhere.
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const QUEUE_KEY = 'rw_voice_note_queue';

const safeParse = (s, fallback) => {
  try {
    return s ? JSON.parse(s) : fallback;
  } catch {
    return fallback;
  }
};

export function newOfflineId() {
  return `vn_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function getQueuedNotes() {
  return safeParse(localStorage.getItem(QUEUE_KEY), []);
}

function writeQueue(notes) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(notes));
}

export function pendingVoiceCount() {
  return getQueuedNotes().length;
}

export function enqueueNote(payload) {
  const note = { ...payload, clientOfflineId: payload.clientOfflineId || newOfflineId(), queuedAt: new Date().toISOString() };
  writeQueue([...getQueuedNotes(), note]);
  return note;
}

export function removeQueuedNote(offlineId) {
  writeQueue(getQueuedNotes().filter((n) => n.clientOfflineId !== offlineId));
}

/**
 * Flush every queued note. Returns { synced, failed, remaining }.
 *   - success → removed
 *   - 4xx     → permanent failure, dropped
 *   - network/5xx → kept for next attempt
 */
export async function syncVoiceQueue(token) {
  if (!token) return { synced: 0, failed: 0, remaining: pendingVoiceCount() };
  const notes = getQueuedNotes();
  if (notes.length === 0) return { synced: 0, failed: 0, remaining: 0 };

  let synced = 0;
  let failed = 0;
  const keep = [];

  for (const n of notes) {
    try {
      await axios.post(`${API_URL}/api/dashboard/voice-notes`, n, {
        headers: { Authorization: `Bearer ${token}` },
      });
      synced += 1;
    } catch (err) {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        failed += 1; // drop permanent failures
      } else {
        keep.push(n); // transient — retry later
      }
    }
  }
  writeQueue(keep);
  return { synced, failed, remaining: keep.length };
}
